import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
export function useJournalEntries(itemId, userId) {
    const [entries, setEntries] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    // Fetch entries for this item
    const fetchEntries = useCallback(async (entryType) => {
        if (!itemId || !userId)
            return;
        setIsLoading(true);
        setError(null);
        try {
            const { data, error: fetchError } = await supabase
                .from('journal')
                .select('*')
                .eq('visionboarditemid', itemId)
                .eq('type', entryType)
                .eq('user_id', userId)
                .order('created_at', { ascending: false });
            if (fetchError)
                throw fetchError;
            setEntries(data || []);
        }
        catch (err) {
            console.error('Error fetching journal entries:', err);
            setError('Failed to load entries');
        }
        finally {
            setIsLoading(false);
        }
    }, [itemId, userId]);
    // Get entries for a specific date
    const getEntriesForDate = useCallback((date, entryType) => {
        return entries.filter((entry) => entry.type === entryType &&
            entry.created_at.split('T')[0] === date);
    }, [entries]);
    // Get unique dates with entries
    const getUniqueDates = useCallback((entryType) => {
        const dates = new Set(entries
            .filter((e) => e.type === entryType)
            .map((e) => e.created_at.split('T')[0]));
        return Array.from(dates).sort().reverse();
    }, [entries]);
    // Create a new entry
    const createEntry = useCallback(async (entryType, title, content, category, tags) => {
        if (!itemId || !userId)
            return false;
        try {
            const { error: insertError } = await supabase.from('journal').insert([
                {
                    visionboarditemid: itemId,
                    user_id: userId,
                    type: entryType,
                    title,
                    entry: content,
                    category,
                    tags: tags || [],
                    created_at: new Date().toISOString(),
                },
            ]);
            if (insertError)
                throw insertError;
            await fetchEntries(entryType);
            return true;
        }
        catch (err) {
            console.error('Error creating journal entry:', err);
            setError('Failed to save entry');
            return false;
        }
    }, [itemId, userId, fetchEntries]);
    // Update an existing entry
    const updateEntry = useCallback(async (entryId, updates) => {
        try {
            const { error: updateError } = await supabase
                .from('journal')
                .update({
                ...updates,
                updated_at: new Date().toISOString(),
            })
                .eq('id', entryId);
            if (updateError)
                throw updateError;
            // Refresh entries
            const entryType = entries.find((e) => e.id === entryId)?.type;
            if (entryType) {
                await fetchEntries(entryType);
            }
            return true;
        }
        catch (err) {
            console.error('Error updating journal entry:', err);
            setError('Failed to update entry');
            return false;
        }
    }, [entries, fetchEntries]);
    // Delete an entry
    const deleteEntry = useCallback(async (entryId) => {
        try {
            const { error: deleteError } = await supabase
                .from('journal')
                .delete()
                .eq('id', entryId);
            if (deleteError)
                throw deleteError;
            setEntries((prev) => prev.filter((e) => e.id !== entryId));
            return true;
        }
        catch (err) {
            console.error('Error deleting journal entry:', err);
            setError('Failed to delete entry');
            return false;
        }
    }, []);
    // Parse CBT entry from text
    const parseCBTEntry = (content) => {
        try {
            // Expect format: "Thought: ...\nChallenge: ...\nReframe: ..."
            const thoughtMatch = content.match(/Thought:\s*(.+?)(?=\nChallenge:|$)/s);
            const challengeMatch = content.match(/Challenge:\s*(.+?)(?=\nReframe:|$)/s);
            const reframeMatch = content.match(/Reframe:\s*(.+?)$/s);
            if (thoughtMatch && challengeMatch && reframeMatch) {
                return {
                    negative_thought: thoughtMatch[1].trim(),
                    challenge: challengeMatch[1].trim(),
                    reframe: reframeMatch[1].trim(),
                };
            }
            return null;
        }
        catch {
            return null;
        }
    };
    // Format CBT entry for display/storage
    const formatCBTEntry = (entry) => {
        return `Thought: ${entry.negative_thought}\nChallenge: ${entry.challenge}\nReframe: ${entry.reframe}`;
    };
    return {
        entries,
        isLoading,
        error,
        fetchEntries,
        getEntriesForDate,
        getUniqueDates,
        createEntry,
        updateEntry,
        deleteEntry,
        parseCBTEntry,
        formatCBTEntry,
    };
}
